import { useEffect, useId, useRef, useState, type ComponentProps } from "react";
import { useTranslation } from "react-i18next";
import { Check, Copy, Eye, EyeOff, LoaderCircle, Trash2, Undo2 } from "lucide-react";
import { Field, FieldContent, FieldDescription, FieldLabel } from "../ui/field";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { InputGroupButton } from "../ui/input-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { ROOM_PASSWORD_MAX_LENGTH } from "../../../shared/room-schema.ts";
import { copyRoomCode } from "../../lib/rooms";
import { cn } from "../../lib/utils";
import { RoomFieldError } from "./room-form";
import {
  canAccessCurrentPasscode,
  derivePasscodeAction,
  type PasscodeAction,
} from "./room-form-values";

function PasscodeButton({
  label,
  ...props
}: ComponentProps<typeof InputGroupButton> & { label: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <InputGroupButton size="icon-xs" aria-label={label} {...props} />
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export function EditPasscodeField({
  hasPassword,
  canCopyCode = false,
  codeAvailable = false,
  loadCode,
  originalPassword,
  action,
  password,
  errors,
  onOriginalChange,
  onValueChange,
  onBlur,
}: {
  hasPassword: boolean;
  canCopyCode?: boolean;
  codeAvailable?: boolean;
  loadCode?: (_signal: AbortSignal) => Promise<string>;
  // null means the stored code has not been retrieved (or cannot be retrieved).
  originalPassword: string | null;
  action: PasscodeAction;
  password: string;
  errors: unknown[];
  onOriginalChange: (_password: string) => void;
  onValueChange: (_password: string, _action: PasscodeAction) => void;
  onBlur: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const request = useRef<AbortController | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [revealed, setRevealed] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [notice, setNotice] = useState<"" | "copied" | "copy-failed" | "load-failed" | "restored">(
    "",
  );
  const callbacks = useRef({ onOriginalChange, onValueChange });
  useEffect(() => {
    callbacks.current = { onOriginalChange, onValueChange };
  }, [onOriginalChange, onValueChange]);
  useEffect(
    () => () => {
      request.current?.abort();
      clearTimeout(timer.current);
    },
    [],
  );

  const changing = action === "change";
  const invalid = errors.some((error) => typeof error === "string");
  const allowed = canAccessCurrentPasscode({ hasPassword, canCopyCode, codeAvailable });
  const unknown = hasPassword && originalPassword === null;
  const fallback = unknown && (!allowed || !loadCode || fetchFailed);
  const pending = unknown && !fallback;
  const hasValue = Boolean(password);
  const copyAvailable = allowed && !fetchFailed && originalPassword !== null;

  // Fetch directly into dialog-local state; the secret never enters Query's cache.
  useEffect(() => {
    if (!unknown || !allowed || !loadCode || fetchFailed) return;
    const controller = new AbortController();
    void loadCode(controller.signal).then(
      (original) => {
        if (controller.signal.aborted) return;
        callbacks.current.onOriginalChange(original);
        callbacks.current.onValueChange(original, "keep");
      },
      () => {
        if (controller.signal.aborted) return;
        setFetchFailed(true);
        setNotice("load-failed");
      },
    );
    return () => controller.abort();
  }, [unknown, allowed, loadCode, fetchFailed]);
  const feedback = notice
    ? t(
        {
          copied: "home.codeCopySuccess",
          "copy-failed": "home.codeCopyFailed",
          "load-failed": "home.codeLoadFailed",
          restored: "home.codeRestored",
        }[notice],
      )
    : "";

  function update(
    value: string,
    next = derivePasscodeAction(hasPassword, originalPassword, value),
  ) {
    clearTimeout(timer.current);
    setNotice("");
    onValueChange(value, next);
  }

  async function copy() {
    if (!copyAvailable || request.current) return;
    const controller = new AbortController();
    request.current = controller;
    clearTimeout(timer.current);
    setNotice("");
    try {
      await copyRoomCode(Promise.resolve(originalPassword!));
      if (!controller.signal.aborted) setNotice("copied");
    } catch {
      if (!controller.signal.aborted) setNotice("copy-failed");
    } finally {
      if (!controller.signal.aborted) {
        request.current = null;
        timer.current = setTimeout(() => setNotice(""), 2500);
      }
    }
  }

  return (
    <Field orientation="responsive" data-invalid={invalid || undefined} className="min-w-0">
      <FieldLabel id={`${id}-label`} htmlFor={`${id}-password`} className="@sm/field-group:mt-2">
        {t("home.password")}
      </FieldLabel>
      <FieldContent>
        <p role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {feedback ||
            t(
              changing
                ? "home.editingCode"
                : action === "remove"
                  ? "home.codeRemovalPending"
                  : hasPassword
                    ? "home.hasCode"
                    : "home.noCode",
            )}
        </p>
        <div className="flex items-center gap-1">
          <div
            role="group"
            aria-labelledby={`${id}-label`}
            data-slot="input-group"
            className="relative w-56 max-w-full"
          >
            <Input
              ref={input}
              id={`${id}-password`}
              name="password"
              type={revealed ? "text" : "password"}
              value={password}
              disabled={pending}
              aria-busy={pending || undefined}
              onBlur={onBlur}
              onChange={(event) => {
                const value = event.target.value;
                update(
                  value === originalPassword ? value : value.slice(0, ROOM_PASSWORD_MAX_LENGTH),
                );
              }}
              aria-invalid={invalid || undefined}
              maxLength={
                password === originalPassword
                  ? Math.max(ROOM_PASSWORD_MAX_LENGTH, password.length)
                  : ROOM_PASSWORD_MAX_LENGTH
              }
              autoComplete="new-password"
              aria-describedby={changing ? `${id}-hint ${id}-error` : undefined}
              className={cn(
                "pr-4",
                hasValue && "pr-16",
                hasValue && action !== "keep" && "pr-24",
                !hasValue && (pending || action !== "keep" || fallback) && "pr-10",
              )}
              placeholder={
                pending ? "" : fallback && action === "keep" ? "••••••" : t("home.noCode")
              }
            />
            <div className="absolute inset-y-0 right-1 flex items-center gap-1">
              {pending ? (
                <LoaderCircle
                  aria-hidden="true"
                  className="mr-1 size-4 animate-spin motion-reduce:animate-none"
                />
              ) : null}
              {hasValue ? (
                <PasscodeButton
                  label={t(revealed ? "home.hideCode" : "home.showCode")}
                  aria-pressed={revealed}
                  disabled={pending}
                  onClick={() => setRevealed((value) => !value)}
                >
                  {revealed ? <EyeOff /> : <Eye />}
                </PasscodeButton>
              ) : null}
              {hasValue || (fallback && action === "keep") ? (
                <PasscodeButton
                  label={t("home.removeCodeAction")}
                  className="text-destructive hover:text-destructive"
                  onClick={() => {
                    update("", hasPassword ? "remove" : "keep");
                    input.current?.focus();
                  }}
                >
                  <Trash2 />
                </PasscodeButton>
              ) : null}
              {action !== "keep" ? (
                <PasscodeButton
                  label={t("home.undoCodeRemoval")}
                  onClick={() => {
                    update(originalPassword ?? "", "keep");
                    setRevealed(false);
                    setNotice("restored");
                    input.current?.focus();
                  }}
                >
                  <Undo2 />
                </PasscodeButton>
              ) : null}
            </div>
          </div>
          {/* Copies the stored passcode, even while the field holds an edit. */}
          {copyAvailable ? (
            <Tooltip open={notice === "copy-failed" || notice === "copied" ? true : undefined}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t("home.copyCurrentCode")}
                  onClick={() => void copy()}
                >
                  {notice === "copied" ? <Check /> : <Copy />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {notice === "copy-failed"
                  ? feedback
                  : t(notice === "copied" ? "home.codeCopySuccess" : "home.copyCurrentCode")}
              </TooltipContent>
            </Tooltip>
          ) : null}
        </div>
        {changing ? (
          <FieldDescription id={`${id}-hint`}>{t("home.editCodeHint")}</FieldDescription>
        ) : null}
        <div id={`${id}-error`}>
          <RoomFieldError errors={errors} />
        </div>
      </FieldContent>
    </Field>
  );
}
